import {
  type ReauthDetails,
  type SignInMethod,
  type SignInRefusal,
  SuperblocksError,
} from "@superblocksteam/shared";

export type SignInErrorReason =
  | SuperblocksError.ReauthRequired
  | SuperblocksError.OrgSignInRequired
  | SuperblocksError.AccountExists
  | SuperblocksError.SignInBlocked;

/** Auth0 authorize params for the button. Without them the button signs out. */
export interface SignInRedirect {
  login_hint?: string;
  prompt: "login";
  screen_hint?: "signup";
  connection?: string;
}

export interface SignInErrorCopy {
  title: string;
  errorMessage: string;
  buttonText: string;
  /** Text inside errorMessage to show in bold. */
  emphasis?: string;
  signIn?: SignInRedirect;
}

const BLOCKED: SignInErrorCopy = {
  title: "We couldn't sign you in",
  errorMessage: "Ask your admin for help.",
  buttonText: "Sign out",
};

const METHOD_LABELS: Record<SignInMethod, string> = {
  password: "password",
  google: "Google",
  sso: "SSO",
};

const reauthCopy = (reauth: ReauthDetails | undefined): SignInErrorCopy => {
  const newEmail = reauth?.newEmail;
  if (!newEmail) {
    return BLOCKED;
  }
  const title = "Your email address changed";
  const updated = `Your organization updated your email to ${newEmail}.`;
  if (reauth.signInMethod === "password") {
    return {
      title,
      errorMessage: `${updated} To access your account, set up a new password to confirm your email.`,
      emphasis: newEmail,
      buttonText: "Set password",
      signIn: { screen_hint: "signup", login_hint: newEmail, prompt: "login" },
    };
  }
  return {
    title,
    errorMessage: `${updated} Sign back in with this new email to continue.`,
    emphasis: newEmail,
    buttonText: "Sign in again",
    signIn: {
      login_hint: newEmail,
      prompt: "login",
      ...(reauth.signInMethod === "google" && { connection: "google-oauth2" }),
    },
  };
};

const orgSignInCopy = ({ connection }: SignInRefusal): SignInErrorCopy =>
  connection
    ? {
        title: "Use your organization's sign-in",
        errorMessage: `This account uses ${connection}. Sign in with ${connection} to continue.`,
        buttonText: `Use ${connection}`,
        signIn: { connection, prompt: "login" },
      }
    : BLOCKED;

const accountExistsCopy = ({
  email,
  methods = [],
}: SignInRefusal): SignInErrorCopy => {
  if (!email) {
    return BLOCKED;
  }
  const usual = methods.length
    ? `: ${methods.map((method) => METHOD_LABELS[method]).join(" or ")}`
    : "";
  return {
    title: "An account already exists",
    errorMessage: `An account already exists for ${email}. Sign in the way you usually do${usual}.`,
    emphasis: email,
    buttonText: "Sign in",
    signIn: { login_hint: email, prompt: "login" },
  };
};

/** Copy for a refused sign-in; a known server reason gets specific guidance. */
export const signInErrorCopy = (
  reason: SignInErrorReason | undefined,
  supportEmailAddress: string,
  reauth?: ReauthDetails,
  signInRefusal: SignInRefusal = {},
): SignInErrorCopy => {
  switch (reason) {
    case SuperblocksError.ReauthRequired:
      return reauthCopy(reauth);
    case SuperblocksError.OrgSignInRequired:
      return orgSignInCopy(signInRefusal);
    case SuperblocksError.AccountExists:
      return accountExistsCopy(signInRefusal);
    case SuperblocksError.SignInBlocked:
      return BLOCKED;
    default:
      return {
        title: "You do not have permission to log in to Superblocks.",
        errorMessage: `Please request access from a Superblocks admin, or contact the Superblocks team at ${supportEmailAddress}.`,
        buttonText: "Retry Login",
      };
  }
};
